import React, { useState, useEffect, useMemo, useRef, useContext, useCallback } from 'react';
import axios from 'axios';
import { UserContext, WorkspaceContext, OrgContext } from '../../objects/Context';
import { ReactComponent as DownloadIcon } from '../../assets/icons/download-icon.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';
import { downloadBSExcel, generateBSExcelBlob } from '../../services/excelModel/excelBSServiceBrowser';
import './BalanceSheet.css';

// ─── Date helpers (UTC-based to match backend bucketing) ─────────────────────
const pad = (n) => String(n).padStart(2, '0');
// Normalize via Date.UTC so month/day overflow (e.g. month=-1 or day=32) wraps
// correctly into the prior/next year.
const utcIso = (y, m, d) => new Date(Date.UTC(y, m, d)).toISOString().slice(0, 10);

const startOfMonthIso   = (d) => utcIso(d.getUTCFullYear(), d.getUTCMonth(), 1);
const startOfQuarterIso = (d) => utcIso(d.getUTCFullYear(), Math.floor(d.getUTCMonth() / 3) * 3, 1);
const startOfYearIso    = (d) => `${d.getUTCFullYear()}-01-01`;

const todayUTC = () => {
  const n = new Date();
  return new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate()));
};

const addMonthsIso = (iso, n) => {
  const d = new Date(iso + 'T00:00:00Z');
  return utcIso(d.getUTCFullYear(), d.getUTCMonth() + n, d.getUTCDate());
};

const addDaysIso = (iso, n) => {
  const d = new Date(iso + 'T00:00:00Z');
  return utcIso(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n);
};

// Format a single "as of" date — endDate is exclusive, so display as endDate-1.
// e.g. endDate '2024-05-01' → label 'Apr 30, 2024'.
const fmtAsOfLabel = (endDate) => {
  if (!endDate) return '';
  const e = new Date(endDate + 'T00:00:00Z');
  const eMinus1 = new Date(e.getTime() - 24 * 60 * 60 * 1000);
  const opts = { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' };
  return eMinus1.toLocaleDateString('en-US', opts);
};

// Format an inclusive range label given an exclusive [start, end). Trend-mode subtitle.
const fmtRangeLabel = ({ startDate, endDate }) => {
  if (!startDate || !endDate) return '';
  const s = new Date(startDate + 'T00:00:00Z');
  const e = new Date(endDate + 'T00:00:00Z');
  const eMinus1 = new Date(e.getTime() - 24 * 60 * 60 * 1000);
  const opts = { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' };
  return `${s.toLocaleDateString('en-US', opts)} – ${eMinus1.toLocaleDateString('en-US', opts)}`;
};

// Trend-mode column header — period_end is exclusive, render as period_end - 1.
// e.g. {startDate:'2024-04-01', endDate:'2024-05-01'} → 'Apr 30, 2024'.
const fmtTrendColumnAsOf = ({ endDate }) => fmtAsOfLabel(endDate);

const fmtCurrency = (v) => {
  if (v == null || isNaN(v)) return '—';
  const n = Math.round(v);
  if (n === 0) return '0';
  const abs = Math.abs(n).toLocaleString('en-US');
  return n < 0 ? `(${abs})` : abs;
};

// ─── View modes ──────────────────────────────────────────────────────────────
const VIEW_MODES = [
  { key: 'total',     label: 'Total',     granularity: 'period' },
  { key: 'monthly',   label: 'Monthly',   granularity: 'monthly' },
  { key: 'quarterly', label: 'Quarterly', granularity: 'quarterly' },
];

// Total-mode "as of" presets (single point in time)
const TOTAL_PRESETS = [
  { key: 'today',                label: 'Today' },
  { key: 'end_of_last_month',    label: 'End of Last Month' },
  { key: 'end_of_this_quarter',  label: 'End of This Quarter' },
  { key: 'end_of_last_quarter',  label: 'End of Last Quarter' },
  { key: 'end_of_last_year',     label: 'End of Last Year' },
];

// Trend-mode range presets (multi-period — each column shows balance at end of bucket)
const TREND_PRESETS = [
  { key: 'last_3m',   label: 'Last 3 Months' },
  { key: 'last_6m',   label: 'Last 6 Months' },
  { key: 'last_12m',  label: 'Last 12 Months' },
  { key: 'ytd',       label: 'Year to Date' },
  { key: 'last_year', label: 'Last Year' },
];

const COMPARE_PRESETS = [
  { key: 'none',          label: 'No Comparison' },
  { key: 'one_month_ago', label: '1 Month Ago' },
  { key: 'one_year_ago',  label: '1 Year Ago' },
];

const TOTAL_PRESET_KEYS = new Set(TOTAL_PRESETS.map(p => p.key));
const TREND_PRESET_KEYS = new Set(TREND_PRESETS.map(p => p.key));

// ─── Range computation ──────────────────────────────────────────────────────
// All ranges are returned as { startDate, endDate } where endDate is exclusive
// — i.e. endDate is the day AFTER the "as of" date the user perceives.
// In Total mode startDate is unused by the SQL but must satisfy the backend's
// `startDate < endDate` validation; we anchor it at start of the relevant
// month/quarter/year so labels still make sense.
function computeRangeFromPreset(preset, viewMode) {
  const today = todayUTC();
  const todayPlus1 = utcIso(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + 1);

  switch (preset) {
    // ── Total-mode (single as-of) presets ─────────────────────────────
    case 'today': {
      const s = startOfMonthIso(today);
      return { startDate: s, endDate: todayPlus1 };
    }
    case 'end_of_last_month': {
      const s = addMonthsIso(startOfMonthIso(today), -1);
      const e = startOfMonthIso(today);
      return { startDate: s, endDate: e };
    }
    case 'end_of_this_quarter': {
      const s = startOfQuarterIso(today);
      const e = addMonthsIso(s, 3);
      return { startDate: s, endDate: e };
    }
    case 'end_of_last_quarter': {
      const e = startOfQuarterIso(today);
      const s = addMonthsIso(e, -3);
      return { startDate: s, endDate: e };
    }
    case 'end_of_last_year': {
      const yr = today.getUTCFullYear() - 1;
      return { startDate: `${yr}-01-01`, endDate: `${today.getUTCFullYear()}-01-01` };
    }
    // ── Trend-mode presets ────────────────────────────────────────────
    case 'ytd': {
      const s = startOfYearIso(today);
      const end = viewMode === 'total' ? todayPlus1 : addMonthsIso(startOfMonthIso(today), 1);
      return { startDate: s, endDate: end };
    }
    case 'last_year': {
      const yr = today.getUTCFullYear() - 1;
      return { startDate: `${yr}-01-01`, endDate: `${today.getUTCFullYear()}-01-01` };
    }
    case 'last_3m':
    case 'last_6m':
    case 'last_12m': {
      // Trailing N months ending with the current (in-progress) month.
      const monthsBack = preset === 'last_3m' ? 3 : preset === 'last_6m' ? 6 : 12;
      const endIso = addMonthsIso(startOfMonthIso(today), 1);
      const startIso = addMonthsIso(endIso, -monthsBack);
      return { startDate: startIso, endDate: endIso };
    }
    default:
      return null;
  }
}

// Shift an ISO date by `months`, clamping the day-of-month so e.g. shifting
// Feb 29 of a leap year back 12 months doesn't silently roll into Mar 1.
// Used by BS comparison since the user's "as of" date may be Feb 29.
const shiftMonthsClampDay = (iso, months) => {
  const d = new Date(iso + 'T00:00:00Z');
  const targetY = d.getUTCFullYear();
  const targetM = d.getUTCMonth() + months;
  const day = d.getUTCDate();
  // Last day of the target month after normalization.
  const lastDayOfTargetMonth = new Date(Date.UTC(targetY, targetM + 1, 0)).getUTCDate();
  return utcIso(targetY, targetM, Math.min(day, lastDayOfTargetMonth));
};

// Compute a comparison "as of" date (endDate, exclusive) given the primary
// endDate. Returns just the endDate as a string — comparison is a single
// point in time on the BS, not a range.
function computeComparisonEndDate(primaryEndDate, mode) {
  if (!primaryEndDate || mode === 'none' || !mode) return null;
  if (mode === 'one_month_ago')  return shiftMonthsClampDay(primaryEndDate, -1);
  if (mode === 'one_year_ago')   return shiftMonthsClampDay(primaryEndDate, -12);
  return null;
}

// ─── Periods (local mirror of backend bucketing) ────────────────────────────
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function buildPeriodsLocal(range, granularity) {
  if (granularity === 'period') {
    return [{ key: range.startDate, label: '', startDate: range.startDate, endDate: range.endDate }];
  }
  const periods = [];
  const startD = new Date(range.startDate + 'T00:00:00Z');
  const endD = new Date(range.endDate + 'T00:00:00Z');
  let cur;
  if (granularity === 'monthly') {
    cur = new Date(Date.UTC(startD.getUTCFullYear(), startD.getUTCMonth(), 1));
  } else {
    cur = new Date(Date.UTC(startD.getUTCFullYear(), Math.floor(startD.getUTCMonth() / 3) * 3, 1));
  }
  while (cur < endD) {
    const y = cur.getUTCFullYear();
    const m = cur.getUTCMonth();
    let periodEnd, label;
    if (granularity === 'monthly') {
      periodEnd = new Date(Date.UTC(y, m + 1, 1));
      label = `${MONTH_NAMES[m]} ${y}`;
    } else {
      periodEnd = new Date(Date.UTC(y, m + 3, 1));
      label = `Q${Math.floor(m / 3) + 1} ${y}`;
    }
    periods.push({
      key: `${y}-${pad(m + 1)}-01`,
      label,
      startDate: cur.toISOString().slice(0, 10),
      endDate: periodEnd.toISOString().slice(0, 10),
    });
    cur = periodEnd;
  }
  return periods.length ? periods : [{ key: range.startDate, label: '', startDate: range.startDate, endDate: range.endDate }];
}

// ─── Variance coloring ───────────────────────────────────────────────────────
const varianceClass = (variance, favorable) => {
  if (variance == null || Math.abs(variance) < 0.5) return '';
  const isPositive = variance > 0;
  const good = (favorable === 'positive') ? isPositive : !isPositive;
  return good ? 'bs-pos' : 'bs-neg';
};

// ─── Account row (recursive, collapsable) ────────────────────────────────────
// When a parent has children and is expanded, values move out of the parent row
// and appear in a bold "Total [name]" subtotal row beneath the children. When
// collapsed, the parent row shows the rolled-up values directly.
const AccountRow = ({ node, depth, periods, showTotal, showCompare, favorable, expanded, onToggle, idPath }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isOpen = expanded[idPath] !== false; // default expanded
  const showValuesOnParent = !hasChildren || !isOpen;
  return (
    <>
      <tr className={`bs-row bs-account ${hasChildren ? 'bs-has-children' : ''} ${depth === 0 ? 'bs-account-top' : ''} ${hasChildren && isOpen ? 'bs-parent-open' : ''}`}>
        <td className="bs-name-cell bs-sticky-col" style={{ paddingLeft: 32 + depth * 16 }}>
          <div className="bs-name-content">
            {hasChildren ? (
              <button className="bs-chevron" onClick={() => onToggle(idPath)} aria-label={isOpen ? 'Collapse' : 'Expand'}>
                <svg className={`bs-chevron-icon ${isOpen ? 'open' : 'closed'}`} viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M3 1.5 L7 5 L3 8.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ) : (
              <span className="bs-chevron-spacer" />
            )}
            <span className="bs-account-name">{node.name}</span>
          </div>
        </td>
        {periods.map((p, i) => (
          <td key={p.key} className="bs-amount-cell">{showValuesOnParent ? fmtCurrency(node.values[i]) : ''}</td>
        ))}
        {showTotal && <td className="bs-amount-cell bs-total-col">{showValuesOnParent ? fmtCurrency(node.total) : ''}</td>}
        {showCompare && <td className="bs-amount-cell bs-cmp">{showValuesOnParent ? fmtCurrency(node.total_comparison) : ''}</td>}
        {showCompare && (
          <td className={`bs-amount-cell bs-variance ${showValuesOnParent ? varianceClass(node.total_variance, favorable) : ''}`}>
            {showValuesOnParent ? fmtCurrency(node.total_variance) : ''}
          </td>
        )}
      </tr>
      {hasChildren && isOpen && (
        <>
          {node.children.map((child) => (
            <AccountRow
              key={child.id}
              node={child}
              depth={depth + 1}
              periods={periods}
              showTotal={showTotal}
              showCompare={showCompare}
              favorable={favorable}
              expanded={expanded}
              onToggle={onToggle}
              idPath={`${idPath}/${child.id}`}
            />
          ))}
          <tr className="bs-row bs-account-subtotal">
            <td className="bs-name-cell bs-sticky-col" style={{ paddingLeft: 32 + depth * 16 }}>
              <div className="bs-name-content">
                <span className="bs-chevron-spacer" />
                <span className="bs-account-subtotal-label">Total {node.name}</span>
              </div>
            </td>
            {periods.map((p, i) => (
              <td key={p.key} className="bs-amount-cell">{fmtCurrency(node.values[i])}</td>
            ))}
            {showTotal && <td className="bs-amount-cell bs-total-col">{fmtCurrency(node.total)}</td>}
            {showCompare && <td className="bs-amount-cell bs-cmp">{fmtCurrency(node.total_comparison)}</td>}
            {showCompare && (
              <td className={`bs-amount-cell bs-variance ${varianceClass(node.total_variance, favorable)}`}>
                {fmtCurrency(node.total_variance)}
              </td>
            )}
          </tr>
        </>
      )}
    </>
  );
};

// ─── Skeleton ────────────────────────────────────────────────────────────────
const SkeletonRow = ({ idx, columnCount }) => (
  <tr className="bs-row bs-skeleton-row">
    <td className="bs-name-cell bs-sticky-col"><div className="bs-skel-bar" style={{ width: `${50 + (idx % 4) * 8}%` }} /></td>
    {Array.from({ length: columnCount - 1 }).map((_, c) => (
      <td key={c} className="bs-amount-cell"><div className="bs-skel-bar" style={{ width: '60%', marginLeft: 'auto' }} /></td>
    ))}
  </tr>
);

// ─── Main page ───────────────────────────────────────────────────────────────
function BalanceSheet() {
  const { user } = useContext(UserContext);
  const { workspace } = useContext(WorkspaceContext);
  const { org } = useContext(OrgContext);

  const [viewMode, setViewMode] = useState('total');
  const granularity = useMemo(() => VIEW_MODES.find(v => v.key === viewMode).granularity, [viewMode]);
  const isTrend = viewMode !== 'total';

  // Range state — for Total mode, range.endDate is the (exclusive) "as of"
  // boundary; for Trend mode it's the full grid range.
  const defaultTotalPreset = 'end_of_last_month';
  const [rangePreset, setRangePreset] = useState(defaultTotalPreset);
  const [range, setRange] = useState(computeRangeFromPreset(defaultTotalPreset, 'total'));
  const [customStart, setCustomStart] = useState(range.startDate);
  const [customEnd, setCustomEnd]     = useState(range.endDate);
  // Total-mode custom "as of" date is the inclusive day the user picks.
  // We store it as the inclusive label and convert to exclusive endDate when applying.
  const [customAsOf, setCustomAsOf]   = useState(addDaysIso(range.endDate, -1));

  // Compare state (only used in 'total' mode)
  const [comparePreset, setComparePreset] = useState('none');
  const [customCompareAsOf, setCustomCompareAsOf] = useState('');

  // Atomic view-mode change: also sync preset + range in the same handler so
  // React batches into a single render (avoids a double-fetch with stale args).
  const handleSetViewMode = (newMode) => {
    if (newMode === viewMode) return;
    const newIsTrend = newMode !== 'total';
    const validNow = newIsTrend ? TREND_PRESET_KEYS.has(rangePreset) : TOTAL_PRESET_KEYS.has(rangePreset);
    const nextPreset = (validNow || rangePreset === 'custom')
      ? rangePreset
      : (newIsTrend ? 'last_12m' : defaultTotalPreset);
    const nextRange = nextPreset === 'custom' ? range : computeRangeFromPreset(nextPreset, newMode);

    setViewMode(newMode);
    if (nextPreset !== rangePreset) setRangePreset(nextPreset);
    if (nextRange) {
      setRange(nextRange);
      setCustomStart(nextRange.startDate);
      setCustomEnd(nextRange.endDate);
      setCustomAsOf(addDaysIso(nextRange.endDate, -1));
    }
  };

  // Atomic preset change: also sync range and custom-date inputs.
  const handleSetRangePreset = (preset) => {
    setRangePreset(preset);
    if (preset !== 'custom') {
      const r = computeRangeFromPreset(preset, viewMode);
      if (r) {
        setRange(r);
        setCustomStart(r.startDate);
        setCustomEnd(r.endDate);
        setCustomAsOf(addDaysIso(r.endDate, -1));
      }
    }
    setRangeOpen(false);
  };

  // Resolve effective comparison "as of" endDate (exclusive)
  const compareEndDate = useMemo(() => {
    if (isTrend) return null;
    if (comparePreset === 'none') return null;
    if (comparePreset === 'custom') {
      if (!customCompareAsOf) return null;
      return addDaysIso(customCompareAsOf, 1);
    }
    return computeComparisonEndDate(range.endDate, comparePreset);
  }, [comparePreset, range.endDate, customCompareAsOf, isTrend]);

  // UI state
  const [rangeOpen, setRangeOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const rangeRef = useRef(null);
  const compareRef = useRef(null);
  const downloadRef = useRef(null);
  const tableWrapperRef = useRef(null);

  // Data state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [isHorizScrolled, setIsHorizScrolled] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const handleClick = (e) => {
      if (rangeRef.current && !rangeRef.current.contains(e.target)) setRangeOpen(false);
      if (compareRef.current && !compareRef.current.contains(e.target)) setCompareOpen(false);
      if (downloadRef.current && !downloadRef.current.contains(e.target)) setDownloadOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Toggle a subtle right-edge shadow on the sticky account column when the
  // table is horizontally scrolled (signals scrollable content beneath).
  useEffect(() => {
    const el = tableWrapperRef.current;
    if (!el) return;
    const onScroll = () => setIsHorizScrolled(el.scrollLeft > 0);
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [data, loading]);

  // Fetch BS. Accepts an AbortSignal so stale requests can be cancelled when
  // filters change rapidly (prevents an older response from clobbering newer data).
  const fetchBS = useCallback(async (signal) => {
    if (!user?.id || !workspace?.id) return;
    if (!range?.startDate || !range?.endDate) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('granularity', granularity);
      params.set('startDate', range.startDate);
      params.set('endDate', range.endDate);
      if (!isTrend && compareEndDate) {
        params.set('compareEndDate', compareEndDate);
      }
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/queries/balance-sheet/${encodeURIComponent(user.id)}/${encodeURIComponent(workspace.id)}?${params.toString()}`;
      const res = await axios.get(url, { withCredentials: true, signal });
      if (res.data?.success) {
        setData(res.data);
      } else {
        setData(null);
        setError('Failed to load balance sheet.');
      }
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return; // Superseded by a newer request — leave existing state alone.
      }
      console.error('Failed to fetch BS:', err);
      setError(err.response?.data?.error || 'Failed to load balance sheet.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id, workspace?.id, range, granularity, compareEndDate, isTrend]);

  useEffect(() => {
    const controller = new AbortController();
    fetchBS(controller.signal);
    return () => controller.abort();
  }, [fetchBS]);

  // Validity flags are derived from the live custom inputs — used both to
  // disable the Apply buttons and to guard the apply handlers.
  const isCustomRangeValid = isTrend
    ? !!(customStart && customEnd && customStart < customEnd)
    : !!customAsOf;
  const isCustomCompareValid = !!customCompareAsOf;

  const applyCustomRange = () => {
    if (!isCustomRangeValid) return;
    if (isTrend) {
      setRange({ startDate: customStart, endDate: customEnd });
      setRangePreset('custom');
      setRangeOpen(false);
    } else {
      // Total: single inclusive "as of" date → exclusive endDate.
      const endDate = addDaysIso(customAsOf, 1);
      const startDate = startOfMonthIso(new Date(customAsOf + 'T00:00:00Z'));
      setRange({ startDate, endDate });
      setCustomStart(startDate);
      setCustomEnd(endDate);
      setRangePreset('custom');
      setRangeOpen(false);
    }
  };

  const applyCustomCompare = () => {
    if (!isCustomCompareValid) return;
    setComparePreset('custom');
    setCompareOpen(false);
  };

  const toggleNode = (idPath) => {
    setExpanded(prev => ({ ...prev, [idPath]: prev[idPath] === false }));
  };

  // Reset all filters & UI state back to defaults.
  const handleReset = () => {
    const defaultRange = computeRangeFromPreset(defaultTotalPreset, 'total');
    setViewMode('total');
    setRangePreset(defaultTotalPreset);
    setRange(defaultRange);
    setCustomStart(defaultRange.startDate);
    setCustomEnd(defaultRange.endDate);
    setCustomAsOf(addDaysIso(defaultRange.endDate, -1));
    setComparePreset('none');
    setCustomCompareAsOf('');
    setExpanded({});
    setRangeOpen(false);
    setCompareOpen(false);
  };

  // ── Download (Excel / PDF) ─────────────────────────────────
  const buildExportFilename = () => {
    const wsName = (workspace?.name || workspace?.title || 'balance_sheet').replace(/\s+/g, '_');
    return `${wsName}_balance_sheet_${new Date().toISOString().slice(0, 10)}`;
  };

  const buildExportMetadata = () => {
    const clientPortal = org?.clientPortal || {};
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const logoDownloadUrl = (clientPortal.logoUrl && org?.id && apiBaseUrl)
      ? `${apiBaseUrl}/api/orgs/logo-file/${org.id}`
      : null;
    return {
      workspaceName: workspace?.name || workspace?.title || '',
      statementName: 'Balance Sheet',
      branding: { ...clientPortal, logoDownloadUrl },
    };
  };

  const handleDownloadExcel = async () => {
    if (isExporting || loading || !data) return;
    setDownloadOpen(false);
    try {
      setIsExporting(true);
      await downloadBSExcel(data, buildExportFilename(), buildExportMetadata());
    } catch (err) {
      console.error('BS Excel export failed:', err);
      alert('Failed to export Balance Sheet. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (isExporting || loading || !data) return;
    setDownloadOpen(false);
    try {
      setIsExporting(true);
      const filename = buildExportFilename();
      const workbookBlob = await generateBSExcelBlob(data, buildExportMetadata());

      const formData = new FormData();
      formData.append('file', workbookBlob, `${filename}.xlsx`);

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/export-pdf`,
        formData,
        { withCredentials: true, responseType: 'blob' }
      );

      // Guard against the server returning a non-PDF (e.g. an HTML/JSON error
      // body) — `responseType: 'blob'` would otherwise hand the user a file
      // with a `.pdf` extension that isn't actually a PDF.
      const contentType = (response.headers?.['content-type'] || response.data?.type || '').toLowerCase();
      if (response.status !== 200 || !contentType.includes('application/pdf')) {
        throw new Error(`Unexpected PDF response (status ${response.status}, type "${contentType || 'unknown'}")`);
      }

      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('BS PDF export failed:', err);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // ── Resolve display state from data + UI ──
  // Use server-returned periods when available; otherwise mirror them locally
  // so the header and skeleton render correctly during initial load.
  const localPeriods = useMemo(() => buildPeriodsLocal(range, granularity), [range, granularity]);
  const periods = data?.periods || localPeriods;
  // Hide the "Total" column for BS — summing point-in-time balances has no
  // meaningful interpretation. (P&L sums activity; BS shows snapshots.)
  const showTotalCol = false;
  const showCompareCols = !isTrend && !!compareEndDate && !!data?.comparisonRange;
  const colCount = 1 + periods.length + (showTotalCol ? 1 : 0) + (showCompareCols ? 2 : 0);

  // ── Labels ──
  const activePresets = isTrend ? TREND_PRESETS : TOTAL_PRESETS;
  const rangeBtnLabel = useMemo(() => {
    if (rangePreset === 'custom') {
      return isTrend ? fmtRangeLabel(range) : fmtAsOfLabel(range.endDate);
    }
    const found = activePresets.find(p => p.key === rangePreset);
    return found ? found.label : (isTrend ? fmtRangeLabel(range) : fmtAsOfLabel(range.endDate));
  }, [rangePreset, range, activePresets, isTrend]);

  const compareBtnLabel = useMemo(() => {
    if (isTrend) return 'Compare unavailable';
    if (comparePreset === 'none') return 'Compare';
    if (comparePreset === 'custom' && compareEndDate) return fmtAsOfLabel(compareEndDate);
    return COMPARE_PRESETS.find(c => c.key === comparePreset)?.label || 'Compare';
  }, [isTrend, comparePreset, compareEndDate]);

  const subtitle = useMemo(() => {
    if (isTrend) {
      // Trend mode: range of period-ends (e.g. "Apr 30 – Apr 30, 2024")
      const left = fmtRangeLabel(range);
      return `${left}`;
    }
    // Total mode: "As of [date]" with optional comparison
    const asOf = fmtAsOfLabel(range.endDate);
    if (showCompareCols) return `As of ${asOf}  vs  ${fmtAsOfLabel(compareEndDate)}`;
    return `As of ${asOf}`;
  }, [isTrend, range, showCompareCols, compareEndDate]);

  // ── Render helpers ──
  const renderHeaderCells = () => (
    <tr>
      <th className="bs-th-name bs-sticky-col">ACCOUNT</th>
      {periods.map(p => (
        <th key={p.key} className="bs-th-amount">
          {isTrend ? p.label : fmtTrendColumnAsOf(p)}
        </th>
      ))}
      {showTotalCol && <th className="bs-th-amount bs-total-col">TOTAL</th>}
      {showCompareCols && <th className="bs-th-amount">{fmtAsOfLabel(compareEndDate)}</th>}
      {showCompareCols && <th className="bs-th-amount">VARIANCE</th>}
    </tr>
  );

  const renderTotalRow = (key, total, opts = {}) => {
    if (!total) return null;
    const cls = `bs-row bs-computed ${opts.kind || ''}`;
    return (
      <tr key={key} className={cls}>
        <td className="bs-name-cell bs-sticky-col bs-computed-name">{total.label}</td>
        {periods.map((p, i) => (
          <td key={p.key} className="bs-amount-cell">{fmtCurrency(total.values[i])}</td>
        ))}
        {showTotalCol && <td className="bs-amount-cell bs-total-col">{fmtCurrency(total.total)}</td>}
        {showCompareCols && <td className="bs-amount-cell bs-cmp">{fmtCurrency(total.total_comparison)}</td>}
        {showCompareCols && (
          <td className={`bs-amount-cell bs-variance ${varianceClass(total.total_variance, total.favorable)}`}>
            {fmtCurrency(total.total_variance)}
          </td>
        )}
      </tr>
    );
  };

  // Render an account-type subgroup (Bank, Accounts Receivable, etc.) — the
  // second hierarchy tier inside a classification section. Collapsible: when
  // closed, group totals appear inline in the group header; when open the
  // accounts are rendered followed by a bold "Total [account_type]" row.
  const renderAccountTypeGroup = (section, group) => {
    const groupPath = `__group__/${section.key}/${group.key}`;
    const isOpen = expanded[groupPath] !== false; // default expanded
    return (
      <React.Fragment key={group.key}>
        <tr className={`bs-row bs-account-type-header ${isOpen ? '' : 'bs-account-type-collapsed'}`}>
          <td className="bs-name-cell bs-sticky-col" style={{ paddingLeft: 16 }}>
            <div className="bs-name-content">
              <button
                className="bs-chevron bs-account-type-chevron"
                onClick={() => toggleNode(groupPath)}
                aria-label={isOpen ? 'Collapse' : 'Expand'}
              >
                <svg className={`bs-chevron-icon ${isOpen ? 'open' : 'closed'}`} viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M3 1.5 L7 5 L3 8.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <span className="bs-account-type-label">{group.label}</span>
            </div>
          </td>
          {periods.map((p, i) => (
            <td key={p.key} className="bs-amount-cell">{!isOpen ? fmtCurrency(group.values[i]) : ''}</td>
          ))}
          {showTotalCol && <td className="bs-amount-cell bs-total-col">{!isOpen ? fmtCurrency(group.total) : ''}</td>}
          {showCompareCols && <td className="bs-amount-cell bs-cmp">{!isOpen ? fmtCurrency(group.total_comparison) : ''}</td>}
          {showCompareCols && (
            <td className={`bs-amount-cell bs-variance ${!isOpen ? varianceClass(group.total_variance, section.favorable) : ''}`}>
              {!isOpen ? fmtCurrency(group.total_variance) : ''}
            </td>
          )}
        </tr>
        {isOpen && (
          <>
            {group.accounts.map(node => (
              <AccountRow
                key={node.id}
                node={node}
                depth={0}
                periods={periods}
                showTotal={showTotalCol}
                showCompare={showCompareCols}
                favorable={section.favorable}
                expanded={expanded}
                onToggle={toggleNode}
                idPath={`${section.key}/${group.key}/${node.id}`}
              />
            ))}
            <tr className="bs-row bs-account-type-total">
              <td className="bs-name-cell bs-sticky-col" style={{ paddingLeft: 16 }}>
                <div className="bs-name-content">
                  <span className="bs-chevron-spacer" />
                  <span className="bs-account-type-total-label">{group.total_label}</span>
                </div>
              </td>
              {periods.map((p, i) => (
                <td key={p.key} className="bs-amount-cell">{fmtCurrency(group.values[i])}</td>
              ))}
              {showTotalCol && <td className="bs-amount-cell bs-total-col">{fmtCurrency(group.total)}</td>}
              {showCompareCols && <td className="bs-amount-cell bs-cmp">{fmtCurrency(group.total_comparison)}</td>}
              {showCompareCols && (
                <td className={`bs-amount-cell bs-variance ${varianceClass(group.total_variance, section.favorable)}`}>
                  {fmtCurrency(group.total_variance)}
                </td>
              )}
            </tr>
          </>
        )}
      </React.Fragment>
    );
  };

  const renderSection = (section) => {
    if (!section) return null;
    // Section paths are namespaced with `__section__/` so they don't collide
    // with account idPaths or group paths.
    const sectionPath = `__section__/${section.key}`;
    const isOpen = expanded[sectionPath] !== false; // default expanded
    // Prefer the new account_type_groups (Bank, AR, etc.); fall back to flat
    // accounts when grouping isn't available (e.g. older API responses).
    const groups = Array.isArray(section.account_type_groups) && section.account_type_groups.length > 0
      ? section.account_type_groups
      : null;
    return (
      <React.Fragment key={section.key}>
        <tr className={`bs-row bs-section-header ${isOpen ? '' : 'bs-section-collapsed'}`}>
          <td className="bs-name-cell bs-sticky-col">
            <div className="bs-section-header-content">
              <button
                className="bs-chevron bs-section-chevron"
                onClick={() => toggleNode(sectionPath)}
                aria-label={isOpen ? 'Collapse' : 'Expand'}
              >
                <svg className={`bs-chevron-icon ${isOpen ? 'open' : 'closed'}`} viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M3 1.5 L7 5 L3 8.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <span className="bs-section-label">{section.label}</span>
            </div>
          </td>
          {periods.map((p, i) => (
            <td key={p.key} className="bs-amount-cell">{!isOpen ? fmtCurrency(section.values[i]) : ''}</td>
          ))}
          {showTotalCol && <td className="bs-amount-cell bs-total-col">{!isOpen ? fmtCurrency(section.total) : ''}</td>}
          {showCompareCols && <td className="bs-amount-cell bs-cmp">{!isOpen ? fmtCurrency(section.total_comparison) : ''}</td>}
          {showCompareCols && (
            <td className={`bs-amount-cell bs-variance ${!isOpen ? varianceClass(section.total_variance, section.favorable) : ''}`}>
              {!isOpen ? fmtCurrency(section.total_variance) : ''}
            </td>
          )}
        </tr>
        {isOpen && (
          <>
            {groups
              ? groups.map(group => renderAccountTypeGroup(section, group))
              : section.accounts.map(node => (
                  <AccountRow
                    key={node.id}
                    node={node}
                    depth={0}
                    periods={periods}
                    showTotal={showTotalCol}
                    showCompare={showCompareCols}
                    favorable={section.favorable}
                    expanded={expanded}
                    onToggle={toggleNode}
                    idPath={`${section.key}/${node.id}`}
                  />
                ))}
            <tr className="bs-row bs-section-total">
              <td className="bs-name-cell bs-sticky-col">{section.total_label}</td>
              {periods.map((p, i) => (
                <td key={p.key} className="bs-amount-cell">{fmtCurrency(section.values[i])}</td>
              ))}
              {showTotalCol && <td className="bs-amount-cell bs-total-col">{fmtCurrency(section.total)}</td>}
              {showCompareCols && <td className="bs-amount-cell bs-cmp">{fmtCurrency(section.total_comparison)}</td>}
              {showCompareCols && (
                <td className={`bs-amount-cell bs-variance ${varianceClass(section.total_variance, section.favorable)}`}>
                  {fmtCurrency(section.total_variance)}
                </td>
              )}
            </tr>
          </>
        )}
      </React.Fragment>
    );
  };

  const sectionByKey = (key) => data?.sections?.find(s => s.key === key);

  // Memoize section lookups so renderSection / conditional subtotals don't
  // traverse data.sections on every reference.
  const assetSection     = sectionByKey('asset');
  const liabilitySection = sectionByKey('liability');
  const equitySection    = sectionByKey('equity');

  return (
    <div className="wrapper bs-wrapper">
      <div className="bs-page">

        {/* ── Toolbar (filters live above the report) ── */}
        <div className="bs-toolbar">
          <div className="bs-toolbar-left">
            <div className="bs-toggle-strip">
              {VIEW_MODES.map(m => (
                <button
                  key={m.key}
                  className={`bs-toggle-btn ${viewMode === m.key ? 'active' : ''}`}
                  onClick={() => handleSetViewMode(m.key)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bs-toolbar-right">
            {/* As-of / Range selector */}
            <div className="bs-dropdown-anchor" ref={rangeRef}>
              <button
                className={`bs-control-btn ${rangePreset !== (isTrend ? 'last_12m' : defaultTotalPreset) ? 'has-filters' : ''}`}
                onClick={() => { setRangeOpen(p => !p); setCompareOpen(false); }}
              >
                {rangeBtnLabel}
                <span className="bs-control-caret" aria-hidden="true">▾</span>
              </button>
              {rangeOpen && (
                <div className="bs-dropdown">
                  <div className="bs-dropdown-heading">{isTrend ? 'Range' : 'As of'}</div>
                  {activePresets.map(p => (
                    <button
                      key={p.key}
                      className={`bs-dropdown-option ${rangePreset === p.key ? 'selected' : ''}`}
                      onClick={() => handleSetRangePreset(p.key)}
                    >
                      {p.label}
                    </button>
                  ))}
                  <div className="bs-dropdown-sep" />
                  <div className="bs-dropdown-heading">{isTrend ? 'Custom Range' : 'Custom Date'}</div>
                  {isTrend ? (
                    <div className="bs-date-row">
                      <input type="date" className="bs-date-input" value={customStart} onChange={e => setCustomStart(e.target.value)} />
                      <span className="bs-date-sep">→</span>
                      <input type="date" className="bs-date-input" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
                    </div>
                  ) : (
                    <div className="bs-date-row">
                      <input type="date" className="bs-date-input" value={customAsOf} onChange={e => setCustomAsOf(e.target.value)} />
                    </div>
                  )}
                  <button
                    className="bs-dropdown-apply"
                    onClick={applyCustomRange}
                    disabled={!isCustomRangeValid}
                    title={isCustomRangeValid ? '' : (isTrend ? 'Pick a start date that is before the end date' : 'Pick an as-of date')}
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>

            {/* Compare selector (greyed out in trend modes) */}
            <div className="bs-dropdown-anchor" ref={compareRef}>
              <button
                className={`bs-control-btn ${comparePreset !== 'none' && !isTrend ? 'has-filters' : ''} ${isTrend ? 'bs-disabled' : ''}`}
                onClick={() => { if (!isTrend) { setCompareOpen(p => !p); setRangeOpen(false); } }}
                disabled={isTrend}
                title={isTrend ? 'Comparison is only available in Total view' : ''}
              >
                {compareBtnLabel}
                <span className="bs-control-caret" aria-hidden="true">▾</span>
              </button>
              {compareOpen && !isTrend && (
                <div className="bs-dropdown">
                  <div className="bs-dropdown-heading">Compare to</div>
                  {COMPARE_PRESETS.map(c => (
                    <button
                      key={c.key}
                      className={`bs-dropdown-option ${comparePreset === c.key ? 'selected' : ''}`}
                      onClick={() => { setComparePreset(c.key); setCompareOpen(false); }}
                    >
                      {c.label}
                    </button>
                  ))}
                  <div className="bs-dropdown-sep" />
                  <div className="bs-dropdown-heading">Custom Date</div>
                  <div className="bs-date-row">
                    <input type="date" className="bs-date-input" value={customCompareAsOf} onChange={e => setCustomCompareAsOf(e.target.value)} />
                  </div>
                  <button
                    className="bs-dropdown-apply"
                    onClick={applyCustomCompare}
                    disabled={!isCustomCompareValid}
                    title={isCustomCompareValid ? '' : 'Pick an as-of date'}
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>

            <button
              className="bs-reset-btn"
              onClick={handleReset}
              title="Reset filters and expansion to defaults"
              aria-label="Reset filters and expansion to defaults"
            >
              <RefreshIcon className="bs-reset-icon" />
            </button>

            <div className="bs-dropdown-anchor" ref={downloadRef}>
              <button
                className="bs-download-btn"
                onClick={() => {
                  if (isExporting || loading || !data) return;
                  setDownloadOpen(p => !p);
                  setRangeOpen(false);
                  setCompareOpen(false);
                }}
                disabled={isExporting || loading || !data}
                title={data ? 'Download Balance Sheet' : 'No data to download yet'}
              >
                {isExporting ? (
                  <span className="bs-download-spinner" aria-hidden="true" />
                ) : (
                  <DownloadIcon className="bs-download-icon" />
                )}
              </button>
              {downloadOpen && (
                <div className="bs-dropdown bs-download-dropdown">
                  <button className="bs-dropdown-option" onClick={handleDownloadPdf}>
                    Download to PDF
                  </button>
                  <button className="bs-dropdown-option" onClick={handleDownloadExcel}>
                    Download to Excel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Statement Report ── */}
        <div className="bs-report-section">
          <div className="bs-report">

            {/* Report header inside the document */}
            <div className="bs-report-head">
              <div className="bs-report-head-main">
                {workspace?.name && <div className="bs-report-eyebrow">{workspace.name}</div>}
                <h1 className="bs-report-title">Balance Sheet</h1>
                <div className="bs-report-period">{subtitle}</div>
              </div>
            </div>

            <div className="bs-report-rule" />

            <div className="bs-table-outer">
              <div className={`bs-table-wrapper ${isHorizScrolled ? 'is-scrolled' : ''}`} ref={tableWrapperRef}>
                <table className="bs-table">
                <thead>
                  {renderHeaderCells()}
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 14 }).map((_, i) => <SkeletonRow key={i} idx={i} columnCount={Math.max(2, colCount)} />)
                  ) : error ? (
                    <tr><td className="bs-empty-row bs-error-row" colSpan={colCount}>{error}</td></tr>
                  ) : !data || !data.sections || data.sections.length === 0 ? (
                    <tr><td className="bs-empty-row" colSpan={colCount}>No balance sheet activity for this date.</td></tr>
                  ) : (
                    <>
                      {renderSection(assetSection)}
                      {renderSection(liabilitySection)}
                      {renderSection(equitySection)}
                      {(liabilitySection || equitySection) &&
                        renderTotalRow('liabilities_and_equity', data.totals?.liabilities_and_equity, { kind: 'bs-subtotal' })}
                    </>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BalanceSheet;
